import { Box, responsive } from '@apia/theme';
import { useState, useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import Field from '../../../../JSApi/constants/Field';
import { TApiaFieldType, TApiaComponent } from '../../../../types';
import IProperty from '../../../../JSApi/constants/IProperty';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';

export interface IFieldWrapper {
  id: string;
  children: React.ReactNode;
  field: TApiaComponent;
  fieldId: string;
  x?: number;
  y?: number;
  cols?: number;
  rows?: number;
  formCols: number;
  fieldType: TApiaFieldType;
  attName: string;
  name?: string;
  valueType: string;
}

const getColEndMidBPoint = (colsSpan: number): string => {
  if (colsSpan <= 2) return 'span 2';
  if (colsSpan >= 2) return 'span 4';
  return 'span 1';
};

function isTrue(str: any) {
  return str !== 'F' && !!str;
}

const FieldWrapper = ({
  id,
  children,
  fieldId,
  x = 1,
  y = 1,
  cols = 1,
  rows = 1,
  formCols,
  attName,
  name,
  valueType,
  field,
  fieldType,
}: IFieldWrapper) => {
  const xmlProps = field.properties;
  const storeProps = usePropsSelector(
    fieldId,
    (currentProps) => {
      const { message, gridHeight, field } = currentProps;

      const cssClass = currentProps[IProperty.PROPERTY_CSS_CLASS];
      const isFileOrTree = ['tree'].includes(field?.fieldType ?? '');
      const hiddenSource = isFileOrTree
        ? field?.properties?.[IProperty.PROPERTY_VISIBILITY_HIDDEN]
        : currentProps[IProperty.PROPERTY_VISIBILITY_HIDDEN];

      return {
        message,
        [IProperty.PROPERTY_CSS_CLASS]: cssClass,
        [IProperty.PROPERTY_VISIBILITY_HIDDEN]: isTrue(hiddenSource),
        gridHeight,
      };
    },
    shallowEqual,
  );

  const props = storeProps ?? xmlProps;
  const [cssClasses, setCssClasses] = useState<string>();

  useEffect(() => {
    if (props) {
      const attNameClass = attName !== undefined ? `att_${attName}` : '';
      const valueTypeClass = valueType !== undefined ? `type_${valueType}` : '';
      const css =
        props[IProperty.PROPERTY_CSS_CLASS] !== null
          ? props[IProperty.PROPERTY_CSS_CLASS]
          : null;
      setCssClasses(
        `col_${cols} row_${rows} ${attNameClass} ${valueTypeClass} ${
          css || ''
        }`.trim(),
      );
    }
  }, [id, attName, x, y, cols, rows, valueType, props]);

  return (
    <Box
      key={`field_wrapper_${id}`}
      data-testid={`wrapper_${name || attName}`}
      className={`${cssClasses ?? ''} ${fieldType} fieldWrapper ${
        storeProps?.message !== null && storeProps?.message !== undefined
          ? 'invalid'
          : ''
      } wrapper_${name || attName}`}
      sx={{
        // COLS
        // [0, '250px', '500px', '700px', '1000px', '1260px', '1580px'],
        gridColumnStart: responsive({ print: x, 0: '1', 2: 'auto', 3: x }),
        gridColumnEnd: responsive({
          print: `span ${cols}`,
          0: `span ${formCols}`,
          2: getColEndMidBPoint(cols),
          3: `span ${cols}`,
        }),

        // ROWS
        gridRowStart: responsive({ print: `${y}`, 0: 'auto', 3: `${y}` }),
        gridRowEnd: !props?.gridHeight ? `span ${rows}` : null,

        overflow: fieldType === Field.TYPE_GRID ? 'hidden' : null,
        height: fieldType === Field.TYPE_BUTTON ? null : '100%',
        display: props[IProperty.PROPERTY_VISIBILITY_HIDDEN] ? 'none' : 'flex',
        width: Field.TYPE_CHECK === fieldType ? 'fit-content' : '100%',
      }}
    >
      {children}
    </Box>
  );
};

export default FieldWrapper;
